import uniq from 'lodash/uniq';

import apollo from 'apollo';

import { createNormalizedMap, handleFetchError } from 'action-utils';
import {
  installedAppsQuery,
  installedClocksQuery,
  installedAppsAndClocksQuery,
  installedAppsClocksAndTilesQuery,
  installedAppsWithTilesQuery,
  installedClocksWithTilesQuery,
  installedAppsAndClocksWithGrantQuery,
  installedAppsClocksAndTilesWithGrantQuery
} from 'queries';
import { AsyncActionHandler } from 'action-handler';
import { reducer as statusReducer } from 'actions/registry/fetch-installed-apps-status';
import { multiReducer as permissionsReducer } from 'actions/registry/update-permissions';

const stateProp = 'installed';

export const reducer = (
  state,
  { entries, apps, tileStatuses, hasGalleryGrant, favorites }
) => {
  let parsedFavorites = favorites && favorites.map(({ id }) => id);

  const { installed } = state;

  if (!parsedFavorites) {
    const removeFavesMap = {};
    const entryFaves = [];

    for (const entry of entries) {
      if (entry.favorite) {
        entryFaves.push(entry.appId);
      } else {
        removeFavesMap[entry.appId] = true;
      }
    }

    parsedFavorites = [
      ...state.favorites.filter((id) => !removeFavesMap[id]),
      ...entryFaves
    ];
  }

  let newInstalledState = {};

  const installedState = {
    entries: installed.entries,
    tileStatuses: installed.tileStatuses
  };

  if (entries) {
    installedState.entries = entries;
  }

  if (tileStatuses && tileStatuses.length) {
    installedState.tileStatuses = tileStatuses;
  }

  if (entries) {
    newInstalledState = {
      ...statusReducer(state, { entries, tileStatuses }),
      ...permissionsReducer(state, { entries }),
      [stateProp]: installedState
    };
  }

  const newState = {
    ...newInstalledState,
    apps: {
      ...state.apps,
      ...createNormalizedMap(apps, state.apps),
      ...createNormalizedMap(favorites, state.apps)
    },
    favorites: uniq(parsedFavorites).filter((item) => !!item),
    tiles: {
      ...state.tiles,
      ...createNormalizedMap(tileStatuses, state.tiles, 'tileId')
    },
    selectedClock: null
  };

  if (hasGalleryGrant !== undefined) {
    newState.galleryGrant = {
      userHasGrant: hasGalleryGrant,
      complete: true
    };
  }

  return newState;
};

export default new AsyncActionHandler({
  name: 'fetch_installed_apps',
  stateProp,
  defaultState: {
    favorites: []
  },
  handler(type) {
    return async (_, getState) => {
      const {
        features: { flags },
        galleryGrant
      } = getState().registry;

      let installedApps;
      let favorites;

      let appsQuery = installedAppsQuery;
      let clocksQuery = installedClocksQuery;
      let appsAndClocksQuery = galleryGrant.complete
        ? installedAppsAndClocksQuery
        : installedAppsAndClocksWithGrantQuery;

      if (flags.enable_tiles) {
        appsQuery = installedAppsWithTilesQuery;
        clocksQuery = installedClocksWithTilesQuery;
        appsAndClocksQuery = galleryGrant.complete
          ? installedAppsClocksAndTilesQuery
          : installedAppsClocksAndTilesWithGrantQuery;
      }

      try {
        const { data } = await apollo.query({
          query: type
            ? type === 'CLOCK'
              ? clocksQuery
              : appsQuery
            : appsAndClocksQuery,
          fetchPolicy: 'network-only',
          variables: type && {
            type
          }
        });
        installedApps = data.installedApps;
        favorites = data.favorites;
      } catch (error) {
        return handleFetchError(error);
      }

      const {
        appStatuses,
        apps,
        tileStatuses,
        hasGalleryGrant
      } = installedApps;
      return {
        entries: appStatuses,
        apps,
        tileStatuses,
        hasGalleryGrant,
        favorites
      };
    };
  },
  reduce: reducer
});
